import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemHeader from './SystemHeader';
import GuideButton from './shared/GuideButton';
import { CungVeDichAPI } from '../services/API';
import { SaleReportSubmissionAPI } from '../services/feature-api';
import { useThangDoanhSo, ThongBaoKhoaThang } from '../utils/thangDoanhSo';

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// Compact money: 705 tr, 1.5 Tỷ, 28k, —. Khớp /f1 (f1-list.js) để 2 màn nhìn giống nhau.
const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (!v) return '—';
  if (v >= 1e9) return (v / 1e9).toFixed(2).replace(/\.?0+$/, '') + ' Tỷ';
  if (v >= 1e6) return (v / 1e6).toFixed(1).replace(/\.?0+$/, '') + ' tr';
  if (v >= 1e3) return (v / 1e3).toFixed(0) + 'k';
  return String(v);
};

// Màu ô lịch doanh số: xanh=duyệt, vàng=chờ, đỏ=từ chối, xám=có tiền chưa nộp, mờ=trống. (copy /f1)
const cellStatusStyle = (status, hasAmount) => {
  if (status === 'approved') return 'bg-green-100 text-green-800 hover:bg-green-200';
  if (status === 'rejected') return 'bg-red-100 text-red-700 hover:bg-red-200';
  if (status === 'pending') return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
  if (hasAmount) return 'bg-gray-50 text-gray-600 hover:bg-gray-100';
  return 'bg-white text-gray-300';
};

const SaleReportDashboardForm = ({ userInfo, onLogout, token, totalPoints, accessToken, onRefreshPoints }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [lastUpdatedDate, setLastUpdatedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [summaryData, setSummaryData] = useState([]); // For 2025 T1-T8 summary data
  const summaryDataRef = useRef([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [yearlyMonths, setYearlyMonths] = useState([]); // 12 tháng doanh số + trạng thái duyệt cho lịch trên cùng
  const [monthDetail, setMonthDetail] = useState(null); // ô tháng đang mở popup; null = đóng

  // Generate month options based on year
  const getMonthOptions = (year) => {
    if (year === '2025') {
      return [
        { value: 'T1-T8', label: 'T1 - T8' },
        { value: '09', label: 'Tháng 9' },
        { value: '10', label: 'Tháng 10' },
        { value: '11', label: 'Tháng 11' },
        { value: '12', label: 'Tháng 12' }
      ];
    } else {
      return [
        { value: '01', label: 'Tháng 1' },
        { value: '02', label: 'Tháng 2' },
        { value: '03', label: 'Tháng 3' },
        { value: '04', label: 'Tháng 4' },
        { value: '05', label: 'Tháng 5' },
        { value: '06', label: 'Tháng 6' },
        { value: '07', label: 'Tháng 7' },
        { value: '08', label: 'Tháng 8' },
        { value: '09', label: 'Tháng 9' },
        { value: '10', label: 'Tháng 10' },
        { value: '11', label: 'Tháng 11' },
        { value: '12', label: 'Tháng 12' }
      ];
    }
  };

  // Initialize with current month and year (or from URL params)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const monthParam = urlParams.get('month');
    const yearParam = urlParams.get('year');
    
    if (monthParam && yearParam) {
      // Use month/year from URL params (coming from edit form)
      setSelectedMonth(monthParam);
      setSelectedYear(yearParam);
    } else {
      // Use current month and year
      const now = new Date();
      setSelectedMonth(String(now.getMonth() + 1).padStart(2, '0'));
      setSelectedYear(String(now.getFullYear()));
    }
  }, []);

  // Initialize summary data for T1-T8 months
  useEffect(() => {
    if (selectedYear === '2025') {
      const initialSummaryData = [
        { month: '01', label: 'Tháng 1', amount: '' },
        { month: '02', label: 'Tháng 2', amount: '' },
        { month: '03', label: 'Tháng 3', amount: '' },
        { month: '04', label: 'Tháng 4', amount: '' },
        { month: '05', label: 'Tháng 5', amount: '' },
        { month: '06', label: 'Tháng 6', amount: '' },
        { month: '07', label: 'Tháng 7', amount: '' },
        { month: '08', label: 'Tháng 8', amount: '' }
      ];
      setSummaryData(initialSummaryData);
      summaryDataRef.current = initialSummaryData;
    }
  }, [selectedYear]);

  // Check if current selection is for 2025 T1-T8 summary mode
  const isSummaryMode = selectedYear === '2025' && selectedMonth === 'T1-T8';

  // Helper function to format number with thousands separator (rounded, no decimals)
  const formatNumber = (value) => {
    if (!value || value === '') return '';
    // Remove all non-numeric characters (including dots/commas used as thousands separators)
    const numericValue = value.replace(/[^\d]/g, '');
    // Convert to number, round to nearest integer, then format with thousands separator
    const roundedValue = Math.round(parseFloat(numericValue) || 0);
    return roundedValue.toLocaleString();
  };

  // Helper function to parse formatted number back to numeric value
  const parseNumber = (formattedValue) => {
    if (!formattedValue) return '';
    // Remove all non-numeric characters (including dots/commas used as thousands separators)
    return formattedValue.replace(/[^\d]/g, '');
  };

  // Load sales report data
  const loadSalesData = useCallback(async (month, year) => {
    if (!accessToken || !month || !year) {
      console.warn('Missing required parameters for loading sales data');
      setInitialLoading(false);
      return;
    }

    // Handle T1-T8 summary mode
    if (month === 'T1-T8') {
      setInitialLoading(true);
      setProducts([]);
      
      try {
        console.log('Loading summary data for T1-T8, year:', year);
        
        // Load sales data for each month T1-T8
        const monthPromises = ['01', '02', '03', '04', '05', '06', '07', '08'].map(async (monthNum) => {
          try {
            const salesResult = await CungVeDichAPI.getSalesReport(monthNum, year, accessToken);
            return {
              month: monthNum,
              amount: salesResult.success && salesResult.salesData && salesResult.salesData.length > 0 
                ? parseFloat(salesResult.salesData[0].amount) || 0  // Parse amount correctly
                : 0
            };
          } catch (error) {
            console.error(`Error loading data for month ${monthNum}:`, error);
            return {
              month: monthNum,
              amount: 0
            };
          }
        });

        const monthResults = await Promise.all(monthPromises);
        
        // Update summary data with loaded amounts - show all months including 0 values
        const filteredSummaryData = summaryDataRef.current
          .map(item => {
            const loadedMonth = monthResults.find(result => result.month === item.month);
            return {
              ...item,
              amount: loadedMonth ? formatNumber(loadedMonth.amount.toString()) : ''
            };
          })
          .filter(item => {
            const amount = parseFloat(parseNumber(item.amount)) || 0;
            return amount >= 0; // Show months with 0 values too
          });
        
        setSummaryData(filteredSummaryData);
        
        console.log('Summary data loaded successfully:', monthResults);
      } catch (error) {
        console.error('Error loading summary data:', error);
        setMessage('Có lỗi xảy ra khi tải dữ liệu tổng hợp: ' + error.message);
        setMessageType('error');
      } finally {
        setInitialLoading(false);
      }
      return;
    }

    try {
      setInitialLoading(true);
      console.log('Loading sales report data for month:', month, 'year:', year);
      
      // Get existing sales report data
      const salesResult = await CungVeDichAPI.getSalesReport(month, year, accessToken);
      
      if (salesResult.success && salesResult.salesData && salesResult.salesData.length > 0) {
        // Get the latest updated date if available
        const salesWithUpdatedAt = salesResult.salesData.filter(sale => sale.saleReportUpdatedAt);
        if (salesWithUpdatedAt.length > 0) {
          const latestUpdate = salesWithUpdatedAt.reduce((latest, sale) => {
            const saleDate = new Date(sale.saleReportUpdatedAt);
            const latestDate = new Date(latest);
            return saleDate > latestDate ? sale.saleReportUpdatedAt : latest;
          }, salesWithUpdatedAt[0].saleReportUpdatedAt);
          
          setLastUpdatedDate(latestUpdate);
        }
        
        // Transform existing sales data to form format and filter products with sales
        const transformedProducts = salesResult.salesData
          .map(sale => ({
            id: sale.productId,
            name: sale.productName,
            code: sale.productCode,
            price: sale.productPrice,
            description: sale.productDescription,
            imageUrl: sale.productImageUrl,
            categoryId: sale.categoryId,
            categoryName: sale.categoryName,
            categoryCode: sale.categoryCode,
            quantity: sale.count?.toString() || '',
            // Divide price by 1000 for display (e.g., 55000 becomes 55)
            formattedPrice: formatNumber(((sale.price || 0) / 1000).toString()),
            salesAmount: formatNumber((parseFloat(sale.amount) || 0).toString()),
            notes: sale.saleReportNotes || ''
          }))
          .filter(product => {
            const quantity = parseFloat(parseNumber(product.quantity)) || 0;
            const salesAmount = parseFloat(parseNumber(product.salesAmount)) || 0;
            return quantity > 0 || salesAmount > 0;
          });
        
        setProducts(transformedProducts);
        console.log('Sales report data loaded successfully:', transformedProducts.length);
      } else {
        // No existing sales data
        console.log('No sales data found for the selected period');
        setProducts([]);
      }
    } catch (error) {
      console.error('Error loading sales data:', error);
      setMessage('Có lỗi xảy ra khi tải dữ liệu: ' + error.message);
      setMessageType('error');
    } finally {
      setInitialLoading(false);
    }
  }, [accessToken]);

  // Load data on component mount
  useEffect(() => {
    if (selectedMonth && selectedYear) {
      loadSalesData(selectedMonth, selectedYear);
    }
  }, [accessToken, selectedMonth, selectedYear, loadSalesData]);

  // Load submission status for current month/year
  useEffect(() => {
    const monthNum = selectedMonth === 'T1-T8' ? 1 : parseInt(selectedMonth);
    const yearNum = parseInt(selectedYear);
    if (!accessToken || !monthNum || !yearNum) return;
    SaleReportSubmissionAPI.getMine(accessToken, monthNum, yearNum).then(result => {
      setSubmission(result.success ? result.data : null);
    }).catch(() => setSubmission(null));
  }, [accessToken, selectedMonth, selectedYear]);

  // Lịch 12 tháng: doanh số + trạng thái duyệt của chính mình theo năm đang chọn (đổi năm → nạp lại).
  useEffect(() => {
    const yearNum = parseInt(selectedYear);
    if (!accessToken || !yearNum) return;
    SaleReportSubmissionAPI.getYearly(accessToken, yearNum)
      .then(r => setYearlyMonths(r.success ? (r.data?.months || []) : []))
      .catch(() => setYearlyMonths([]));
  }, [accessToken, selectedYear]);

  // Handle month/year change
  const handleMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
    setMessage(''); // Clear any existing messages
  };

  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);
    setMessage(''); // Clear any existing messages
  };

  const handleEditSalesReport = () => {
    // Navigate to edit form with token and preserve month/year selection
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    const params = new URLSearchParams();
    if (token) params.append('token', token);
    if (selectedMonth) params.append('month', selectedMonth);
    if (selectedYear) params.append('year', selectedYear);

    navigate(`/doanh-so/chinh-sua?${params.toString()}`);
  };

  const handleGoToProofUpload = () => {
    // Navigate directly to step 2 (proof upload) without going through the edit form
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    const params = new URLSearchParams();
    if (token) params.append('token', token);
    if (selectedMonth) params.append('month', selectedMonth);
    if (selectedYear) params.append('year', selectedYear);
    params.append('step', '2');

    navigate(`/doanh-so/chinh-sua?${params.toString()}`);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const isApproved = submission?.companyApproved === 'approved';
  const hasData = products.length > 0;
  const { duocMo, thongBao } = useThangDoanhSo(selectedMonth, selectedYear);

  return (
    <div className="min-h-screen bg-gray-50">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Báo cáo Doanh số"
        pageSubtitle="Xem báo cáo doanh số theo tháng"
        onRefreshPoints={onRefreshPoints}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-4 lg:px-6">
        {/* Top bar: Quay lại + bộ lọc tháng/năm + Hướng dẫn — một hàng duy nhất */}
        <div className="flex items-center gap-2 pt-2 pb-1">
          <button onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors flex-shrink-0"
            style={{ color: '#FF5722', background: '#FF572212' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            <span>Quay lại</span>
          </button>

          {/* Tháng */}
          <select
            value={selectedMonth}
            onChange={(e) => handleMonthChange(e.target.value)}
            className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
          >
            <option value="">Tháng</option>
            {getMonthOptions(selectedYear).map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>

          {/* Năm */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs text-gray-500 whitespace-nowrap">Năm</span>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              min="2020"
              max="2030"
              className="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            />
          </div>

          <GuideButton slug="huong-dan-doanh-so" />
        </div>

        {/* Lịch trạng thái doanh số 12 tháng — nhìn nhanh tháng nào đã duyệt / bị từ chối + tại sao */}
        <div className="bg-white rounded-lg shadow-sm border p-2 mb-2">
          <div className="grid grid-cols-4 gap-1.5">
            {MONTHS.map(m => {
              const cell = yearlyMonths.find(x => x.month === m) || { month: m, amount: 0, status: null };
              const hasAmount = cell.amount > 0;
              // Bấm được khi có doanh số HOẶC có trạng thái duyệt (vd pending/rejected dù doanh số 0) → để user xem "tại sao".
              const interactive = hasAmount || !!cell.status;
              return (
                <button
                  key={m}
                  type="button"
                  disabled={!interactive}
                  onClick={() => interactive && setMonthDetail(cell)}
                  className={`rounded-md border border-gray-200 px-1 py-1.5 text-center transition-colors ${cellStatusStyle(cell.status, hasAmount)} ${interactive ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div className="text-[10px] font-semibold opacity-60 leading-none">T{m}</div>
                  <div className="text-[13px] font-bold leading-tight mt-0.5">{hasAmount ? fmtCompact(cell.amount) : '—'}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-[9.5px] text-gray-500 flex-wrap">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block rounded-sm bg-green-100 border border-green-300" /> Đã duyệt</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block rounded-sm bg-yellow-100 border border-yellow-300" /> Chờ duyệt</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block rounded-sm bg-red-100 border border-red-300" /> Từ chối</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block rounded-sm bg-gray-50 border border-gray-300" /> Chưa nộp</span>
            <span className="ml-auto italic">Chạm tháng để xem chi tiết</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border flex flex-col h-[calc(100vh-380px)]">

          <div className="flex flex-col flex-1">
            {/* Message Display */}
            {message && (
              <div className={`mx-6 mt-6 p-4 rounded-lg flex-shrink-0 ${
                messageType === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {message}
              </div>
            )}

            {/* Summary Statistics - Compact */}
            <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0 bg-blue-50">
              <div className="grid grid-cols-2 gap-2">
                {!isSummaryMode ? (
                  <>
                    <div className="bg-white rounded p-2 border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mr-1">
                            <svg className="w-2 h-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <span className="text-xs font-medium text-gray-500">Tổng số lượng</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">
                          {products.reduce((total, product) => {
                            const quantity = parseFloat(parseNumber(product.quantity)) || 0;
                            return total + quantity;
                          }, 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="bg-white rounded p-2 border border-green-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mr-1">
                            <svg className="w-2 h-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                          </div>
                          <span className="text-xs font-medium text-gray-500">Doanh số</span>
                        </div>
                        <span className="text-xs font-semibold text-gray-900">
                          {formatNumber(products.reduce((total, product) => {
                            const amount = parseFloat(parseNumber(product.salesAmount)) || 0;
                            return total + amount;
                          }, 0).toString())}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded p-2 border border-green-200 col-span-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center mr-1">
                          <svg className="w-2 h-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <span className="text-xs font-medium text-gray-500">Tổng doanh số T1-T8</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-900">
                        {formatNumber(summaryData.reduce((total, item) => {
                          const amount = parseFloat(parseNumber(item.amount)) || 0;
                          return total + amount;
                        }, 0).toString())}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Products List or Summary Display (Readonly) */}
            <div className="flex-1 overflow-y-auto">
              {isSummaryMode ? (
                // Summary Mode - Show monthly totals
                <div className="p-4">
                  {summaryData.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Không có dữ liệu doanh số cho kỳ T1-T8</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {summaryData.map((item) => (
                        <div key={item.month} className="bg-white border border-gray-200 rounded-lg p-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {item.label}
                          </label>
                          <div className="w-full px-3 py-2 text-sm bg-gray-100 border border-gray-200 rounded">
                            {item.amount || '0'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Normal Mode - Show products table (readonly)
                <>
                  {initialLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-3 text-gray-600">Đang tải danh sách sản phẩm...</span>
                    </div>
                  ) : products.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Không có dữ liệu doanh số cho kỳ này</p>
                    </div>
                  ) : (
                    <div className="overflow-y-auto max-h-[calc(100vh-450px)] sm:max-h-[calc(100vh-400px)]">
                      <div className="p-4">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0 z-50 shadow-lg" style={{ position: 'sticky', top: 0 }}>
                            <tr className="border-b-2 border-gray-300">
                              <th className="pb-2 font-semibold text-left text-gray-600 bg-gray-50">Sản phẩm</th>
                              <th className="pb-2 px-1 font-semibold text-center text-gray-600 bg-gray-50">Số lượng</th>
                              <th className="pb-2 px-1 font-semibold text-center text-gray-600 bg-gray-50">Đơn giá</th>
                              <th className="pb-2 font-semibold text-right text-gray-600 bg-gray-50">Thành tiền</th>
                            </tr>
                          </thead>
                          <tbody>
                            {products.map((product, index) => (
                              <tr key={product.id} className="border-b border-gray-200 last:border-b-0">
                                {/* Product Name */}
                                <td className="py-2 pr-1 align-top">
                                  <div className="flex items-center space-x-2">
                                    {/*{product.imageUrl && (*/}
                                    {/*  <div */}
                                    {/*    className="w-8 h-8 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"*/}
                                    {/*    onClick={() => handleImageClick(product.imageUrl, product.name, product.description)}*/}
                                    {/*  >*/}
                                    {/*    <img */}
                                    {/*      src={product.imageUrl} */}
                                    {/*      alt={product.name}*/}
                                    {/*      className="w-full h-full object-cover rounded border border-gray-200"*/}
                                    {/*      onError={(e) => {*/}
                                    {/*        e.target.style.display = 'none';*/}
                                    {/*      }}*/}
                                    {/*    />*/}
                                    {/*  </div>*/}
                                    {/*)}*/}
                                    <div className="text-sm font-medium text-gray-900 flex-1">
                                      {index + 1}. {product.name}
                                    </div>
                                  </div>
                                </td>

                                {/* Quantity (Readonly) */}
                                <td className="py-2 px-1 text-center align-top">
                                  {/*<div className="w-full px-2 py-1 text-sm bg-gray-100 border border-gray-200 rounded text-center" style={{ minWidth: '60px' }}>*/}
                                    {product.quantity}
                                  {/*</div>*/}
                                </td>

                                {/* Price (Readonly) */}
                                <td className="py-2 px-1 text-center align-top">
                                  {/*<div className="w-full px-2 py-1 text-sm bg-gray-100 border border-gray-200 rounded text-center" style={{ minWidth: '80px' }}>*/}
                                    {product.formattedPrice}
                                  {/*</div>*/}
                                </td>

                                {/* Sales Amount (Readonly) */}
                                <td className="py-2 pl-1 text-right font-medium align-top">
                                  {/*<div className="w-full px-2 py-1 text-sm bg-gray-100 border border-gray-200 rounded text-right" style={{ minWidth: '100px' }}>*/}
                                    {product.salesAmount}
                                  {/*</div>*/}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Ngày cập nhật doanh số gần nhất - chỉ hiển thị cho chế độ tháng bình thường */}
            {!isSummaryMode && lastUpdatedDate && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div className="text-xs text-green-700">
                    <span className="font-medium">Ngày cập nhật doanh số gần nhất:</span> {new Date(lastUpdatedDate).toLocaleDateString('vi-VN', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Submission status banner */}
            {submission && (
              <div className={`mx-4 mb-3 px-4 py-3 rounded-lg text-sm flex items-start gap-2 ${
                submission.companyApproved === 'approved'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : submission.companyApproved === 'rejected'
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
              }`}>
                <span className="text-lg leading-none">
                  {submission.companyApproved === 'approved' ? '✅' : submission.companyApproved === 'rejected' ? '❌' : '⏳'}
                </span>
                <div className="flex-1">
                  <span className="font-medium">
                    {submission.companyApproved === 'approved'
                      ? 'Doanh số đã được duyệt'
                      : submission.companyApproved === 'rejected'
                      ? 'Doanh số bị từ chối'
                      : `Doanh số tháng ${selectedMonth}/${selectedYear} đang chờ được duyệt`}
                  </span>
                  {submission.approvalNotes && (
                    <p className="text-xs mt-0.5 opacity-80">{submission.approvalNotes}</p>
                  )}
                </div>
                {submission.proofImage && (
                  <a href={submission.proofImage} target="_blank" rel="noreferrer"
                    className="text-xs underline opacity-70 hover:opacity-100 whitespace-nowrap">Xem ảnh</a>
                )}
              </div>
            )}
            {/* Thiếu ảnh bằng chứng — nút nổi bật */}
            {!submission && !isSummaryMode && hasData && (
              <button
                type="button"
                onClick={handleGoToProofUpload}
                className="mx-4 mb-3 flex items-center justify-between px-4 py-2.5 rounded-lg font-medium transition-colors"
                style={{ background: '#FFF3E0', border: '1.5px solid #FF9800', color: '#E65100' }}
              >
                <span className="flex items-center gap-1.5 text-sm">
                  <span>📎</span>
                  <span>Thiếu ảnh bằng chứng</span>
                </span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: '#FF9800', color: '#fff' }}>
                  Bổ sung →
                </span>
              </button>
            )}

            {!isApproved && !duocMo && <ThongBaoKhoaThang month={selectedMonth} year={selectedYear} thongBao={thongBao} />}
            {/* Action Button - Fixed at bottom — ẩn nếu đã được duyệt hoặc tháng chưa mở cập nhật */}
            {!isApproved && duocMo && (
              <div className="flex-shrink-0 border-t border-gray-200 bg-white py-3 px-4">
                <button
                  type="button"
                  onClick={handleEditSalesReport}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  {hasData ? 'Cập nhật lại đơn cuối tháng' : 'Chốt Đơn Cuối Tháng'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Popup chi tiết 1 tháng từ lịch — trạng thái duyệt + lý do từ chối + số tiền */}
      {monthDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setMonthDetail(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className={`px-5 py-4 border-b ${
              monthDetail.status === 'approved' ? 'bg-green-50 border-green-100'
                : monthDetail.status === 'pending' ? 'bg-yellow-50 border-yellow-100'
                : monthDetail.status === 'rejected' ? 'bg-red-50 border-red-100'
                : 'bg-gray-50 border-gray-100'
            }`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-gray-900 leading-tight">Doanh số T{monthDetail.month}/{selectedYear}</h3>
                  <p className="text-xs mt-1 font-semibold">
                    {monthDetail.status === 'approved' && <span className="text-green-700">✅ Đã duyệt</span>}
                    {monthDetail.status === 'pending' && <span className="text-yellow-700">⏳ Chờ duyệt</span>}
                    {monthDetail.status === 'rejected' && <span className="text-red-700">❌ Bị từ chối</span>}
                    {!monthDetail.status && <span className="text-gray-500">Chưa nộp duyệt</span>}
                  </p>
                </div>
                <button onClick={() => setMonthDetail(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-gray-500">Doanh số tháng</span>
                <span className="text-xl font-bold text-gray-900">{(Number(monthDetail.amount) || 0).toLocaleString('vi-VN')}đ</span>
              </div>
              {monthDetail.approvalNotes && (
                <div className={`mt-3 p-3 rounded-lg border ${
                  monthDetail.status === 'rejected' ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'
                }`}>
                  <p className={`text-xs font-semibold mb-0.5 ${monthDetail.status === 'rejected' ? 'text-orange-800' : 'text-blue-800'}`}>
                    {monthDetail.status === 'rejected' ? 'Lý do từ chối:' : 'Ghi chú:'}
                  </p>
                  <p className={`text-sm ${monthDetail.status === 'rejected' ? 'text-orange-900' : 'text-blue-900'}`}>{monthDetail.approvalNotes}</p>
                </div>
              )}
              {monthDetail.proofImage && (
                <a href={monthDetail.proofImage} target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 underline hover:text-blue-800">
                  📎 Xem ảnh bằng chứng
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{selectedImage.name}</h3>
                <button
                  onClick={closeImageModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.name}
                className="w-full h-auto max-h-[60vh] object-contain rounded-lg mb-4"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                }}
              />
              {selectedImage.description && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Mô tả sản phẩm:</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {selectedImage.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleReportDashboardForm;
